import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import JSEncrypt from 'jsencrypt';
import OtpInput from './OtpInput';
import OAuthButton from './OAuthButton';

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  text-align: center;
  margin-bottom: 8px;
  line-height: 32px;
`;

const Subtext = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.textSecondary};
  text-align: center;
  line-height: 24px;
  margin-bottom: 24px;
`;

const EmailHighlight = styled.span`
  color: ${p => p.theme.text};
  font-weight: 500;
`;

const ResendRow = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  line-height: 18px;
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.accent};
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  cursor: pointer;
  padding: 0;

  &:hover { opacity: 0.8; }
  &:disabled {
    color: ${p => p.theme.textTertiary};
    cursor: default;
  }
`;

const CancelRow = styled.div`
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  line-height: 18px;
`;

const CancelLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.textTertiary};
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover { color: ${p => p.theme.textSecondary}; }
`;

function encryptOtp(base64PublicKey, otpCode) {
  var encrypt = new JSEncrypt();
  encrypt.setPublicKey(atob(base64PublicKey));
  // JSEncrypt.encrypt() returns base64-encoded ciphertext already — no extra btoa
  return encrypt.encrypt(otpCode.toString());
}

// Parent owns the request side. onVerify and onResend are async functions:
//   - resolve → the parent has handled success (typically a step transition)
//   - reject with Error → the error message is shown inline in the OTP input
// onCancel is optional; when provided a Cancel link is rendered below resend.
export default function OtpStep({ email, publicKey, resendAt, onVerify, onResend, onCancel }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const submitted = useRef(false);

  useEffect(() => {
    if (!resendAt) return;

    var update = function() {
      var remaining = Math.max(0, resendAt - Math.floor(Date.now() / 1000));
      setCountdown(remaining);
    };

    update();
    var interval = setInterval(update, 1000);
    return function() { clearInterval(interval); };
  }, [resendAt]);

  const handleSubmit = useCallback(async function(e) {
    if (e) e.preventDefault();

    if (otp.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      var encodedOtp = encryptOtp(publicKey, otp);
      await onVerify(encodedOtp);
      // On success the parent transitions away; this component unmounts.
    } catch (err) {
      submitted.current = false;
      setOtp('');
      setError((err && err.message) || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [otp, publicKey, onVerify]);

  useEffect(() => {
    if (otp.length === 6 && !submitted.current && !loading) {
      submitted.current = true;
      handleSubmit();
    }
  }, [otp, handleSubmit, loading]);

  const handleResend = useCallback(async function() {
    setResending(true);
    setError(null);
    try {
      await onResend();
      setOtp('');
      submitted.current = false;
    } catch (err) {
      setError((err && err.message) || 'Could not resend code.');
    } finally {
      setResending(false);
    }
  }, [onResend]);

  return (
    <form onSubmit={handleSubmit}>
      <Heading>Check your email</Heading>
      <Subtext>
        We sent a code to <EmailHighlight>{email}</EmailHighlight>
      </Subtext>

      <OtpInput
        value={otp}
        onChange={function(val) { setOtp(val); setError(null); submitted.current = false; }}
        error={error}
        disabled={loading}
      />

      <OAuthButton type="submit" loading={loading} disabled={otp.length !== 6}>
        Verify code
      </OAuthButton>

      <ResendRow>
        {countdown > 0 ? (
          <span>Resend available in {countdown}s</span>
        ) : (
          <>
            Didn&apos;t get a code?{' '}
            <ResendLink type="button" onClick={handleResend} disabled={resending}>
              {resending ? 'Sending...' : 'Resend'}
            </ResendLink>
          </>
        )}
      </ResendRow>

      {onCancel && (
        <CancelRow>
          <CancelLink type="button" onClick={onCancel}>Cancel</CancelLink>
        </CancelRow>
      )}
    </form>
  );
}
